import { API_BASE } from './backendUrls';
import { formFetch } from './formPreflight';
import { sessionEndpoints } from './portalSession';

export const SESSION_UNAVAILABLE_EVENT = 'bnw-session-unavailable';
export const SESSION_INVALID_EVENT = 'bnw-session-invalid';
const refreshPromises = {};
export const MASTER_UNAVAILABLE_EVENT = "bnw-master-unavailable";
export const MASTER_INVALID_EVENT = "bnw-master-invalid";
const sessionVersions = { tenant: 0, master: 0 };
export const invalidateSessionRequests = (scope = 'tenant') => { sessionVersions[scope]++; };
const assertCurrentSession = (version, scope = 'tenant') => {
  if (version !== sessionVersions[scope]) {
    const error = new Error('Session changed while the request was in progress.');
    error.code = 'SESSION_SUPERSEDED';
    throw error;
  }
};

const notify = (name, error) => {
  if (error.scope === "master") name = name === SESSION_INVALID_EVENT ? MASTER_INVALID_EVENT : MASTER_UNAVAILABLE_EVENT;
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(name, { detail: error }));
};
export const isInvalidSession = error => error?.code === 'SESSION_EXPIRED';
export function sessionUnavailable(cause, status) {
  const error = new Error('Unable to connect to the server. Check your connection and try again.');
  error.name = 'SessionUnavailableError';
  error.code = 'SESSION_UNAVAILABLE';
  error.retryable = true;
  error.status = status;
  error.cause = cause;
  return error;
}
const invalidSession = (status, data) => {
  const error = new Error(data?.message || 'Your session has expired. Please sign in again.');
  error.name = 'SessionExpiredError';
  error.code = 'SESSION_EXPIRED';
  error.status = status;
  error.data = data;
  return error;
};
export const confirmedInvalidResponse = (response, data) => response.status === 401 ||
  (response.status === 403 && data?.code === 'SUBSCRIPTION_INACTIVE') ||
  (response.status === 404 && data?.code === 'TENANT_NOT_FOUND');

export async function authRequest(url, options = {}) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  options.signal?.addEventListener('abort', abort, { once: true });
  if (options.signal?.aborted) controller.abort();
  const timer = setTimeout(abort, 10000);
  try {
    return await formFetch(`${API_BASE}${url}`, { ...options, credentials: 'include', signal: controller.signal });
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', abort);
  }
}

export function refreshAccessToken(scope = "tenant") {
  if (!refreshPromises[scope]) {
    const version = sessionVersions[scope];
    refreshPromises[scope] = (async () => {
      let response;
      try { response = await authRequest(scope === 'master' ? '/master/auth/refresh' : sessionEndpoints().refresh, { method: 'POST' }); }
      catch (cause) { assertCurrentSession(version, scope); throw sessionUnavailable(cause); }
      assertCurrentSession(version, scope);
      if (response.ok) return;
      let data;
      try { data = await response.json(); } catch { data = null; }
      assertCurrentSession(version, scope);
      if (confirmedInvalidResponse(response, data) || (scope === 'master' && response.status === 403)) {
        const error = invalidSession(response.status, data);
        error.scope = scope;
        notify(SESSION_INVALID_EVENT, error);
        throw error;
      }
      throw sessionUnavailable(null, response.status);
    })().catch(error => {
      error.scope = scope;
      if (!isInvalidSession(error) && error.code !== 'SESSION_SUPERSEDED') notify(SESSION_UNAVAILABLE_EVENT, error);
      throw error;
    }).finally(() => { delete refreshPromises[scope]; });
  }
  return refreshPromises[scope];
}

export const shouldRefreshSession = url => !/\/(?:login|refresh|register|forgot-password|reset-password|logout)$/.test(String(url).split('?')[0]);

export async function sessionFetch(url, options = {}) {
  const path = String(url).split('?')[0];
  const scope = path.startsWith('/master/') ? 'master' : 'tenant';
  const version = sessionVersions[scope];
  const checkVersion = () => { if (shouldRefreshSession(url)) assertCurrentSession(version, scope); };
  const request = () => path === '/auth/me' || path === '/master/auth/me'
    ? authRequest(url, options)
    : formFetch(`${API_BASE}${url}`, { ...options, credentials: 'include' });
  let response;
  try { response = await request(); }
  catch (cause) {
    if (options.signal?.aborted || (!(cause instanceof TypeError) && cause?.name !== 'AbortError')) throw cause;
    checkVersion();
    const error = sessionUnavailable(cause);
    error.scope = scope;
    notify(SESSION_UNAVAILABLE_EVENT, error);
    throw error;
  }
  checkVersion();
  if (response.status === 401 && shouldRefreshSession(url)) {
    await refreshAccessToken(scope);
    checkVersion();
    try { response = await request(); }
    catch (cause) {
      if (options.signal?.aborted || (!(cause instanceof TypeError) && cause?.name !== 'AbortError')) throw cause;
      checkVersion();
    const error = sessionUnavailable(cause);
    error.scope = scope;
      notify(SESSION_UNAVAILABLE_EVENT, error);
      throw error;
    }
    checkVersion();
    if (response.status === 401) {
      const error = invalidSession(401);
      error.scope = scope;
      notify(SESSION_INVALID_EVENT, error);
      throw error;
    }
  }
  return response;
}

